import { useTranslation } from 'react-i18next';
import type { PopularBrandsModule } from 'types/capi/modules';
import type {
  Image as ImageType,
  InternalLink as CapiInternalLink
} from 'types/capi/shared';

import Image, { smallImageOptions } from '~/components/Image';
import Tracked from '~/components/Tracked';
import useEnv from '~/hooks/useEnv';
import useNavigationLinkInteraction from '~/hooks/useNavigationLinkInteraction';
import useRegion from '~/hooks/useRegion';

function CarouselItem({
  image,
  link,
  modulePosition,
  position
}: {
  image: ImageType;
  link: CapiInternalLink;
  modulePosition: number;
  position: number;
}) {
  const fireNavigationLink = useNavigationLinkInteraction('module');

  return (
    <Tracked.InternalLink
      to={link.path}
      variant="none"
      pagePosition="Homepage Popular Brands"
      name="BRAND_CARD"
      onClick={() =>
        fireNavigationLink(link.path, position, {
          label: link.text,
          modulePosition,
          moduleType: 'homepage-popular-brands'
        })
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex size-28 items-center justify-center rounded-full bg-white p-2 md:size-[160px] md:p-5 md:py-12">
          <Image
            src={image.url}
            className="rounded-full p-1"
            alt={image.alt}
            {...smallImageOptions({ pad: true })}
          />
        </div>
      </div>
    </Tracked.InternalLink>
  );
}

type Props = {
  brands: PopularBrandsModule['items'];
  modulePosition: number;
};

function Carousel({ brands, modulePosition }: Props) {
  return (
    <div className="carousel-width no-scrollbar flex items-start gap-2 pb-4 md:justify-between">
      {brands.map((sb, i) => (
        <CarouselItem
          image={sb.image}
          link={sb.link}
          key={sb.link.text}
          modulePosition={modulePosition}
          position={i + 1}
        />
      ))}
    </div>
  );
}

function ViewAllBrandsButton() {
  const { t } = useTranslation('home');
  const region = useRegion();
  const env = useEnv();

  return (
    <>
      {region.code === 'GB' ? (
        <div>
          <Tracked.ExternalLink
            variant="transparent-black-border"
            className="flex items-center justify-center"
            pagePosition="Homepage Popular Brands"
            name="VIEW_ALL"
            href={`${env.SITE_BASE_URL}/brands/`}
          >
            View all brands
          </Tracked.ExternalLink>
        </div>
      ) : (
        <div className="w-48">
          <Tracked.InternalLink
            variant="transparent-black-border"
            className="flex items-center justify-center"
            pagePosition="Homepage Popular Brands"
            name="VIEW_ALL"
            to="/release-dates/"
          >
            {t('viewAllBrands')}
          </Tracked.InternalLink>
        </div>
      )}
    </>
  );
}

export default function PopularBrands({ brands, modulePosition }: Props) {
  const { t } = useTranslation('home');

  return (
    <div className="w-full bg-grey-panel">
      <div className="content-width flex flex-col items-center justify-center gap-6 py-10 md:mx-auto md:py-20">
        <div className="flex flex-col justify-center gap-6 self-stretch py-0">
          <h2 className="text-title-xl text-center">{t('popularBrands')}</h2>
          <div className="flex flex-col items-center gap-8">
            <Carousel brands={brands} modulePosition={modulePosition} />
            <ViewAllBrandsButton />
          </div>
        </div>
      </div>
    </div>
  );
}
